import {api} from "../api";

import {Combobox} from "../ui/combobox";
import {IsSubstituteRequiredResponse, PartialUser, PartialWorkingTimeType} from "@/shared/timr";
import {DurationUnit} from "@/shared/timrTypes";

const template = `
		<div class="form-group">
			<label class="control-label col-xs-2 text-nowrap">
				${messages["substituteSelection.substitute"]}
			</label>
			<div class="col-xs-10">
				<div class="input-group">
					<input name="substitute.id" type="hidden" data-substitute-combobox-id />
					<input type="text" class="form-control" data-substitute-combobox-filter />
					<span class="input-group-btn">
						<button type="button" class="btn btn-default btn-icon" tabindex="-1" data-substitute-combobox-caret>
							<i class="fas fa-caret-down"></i>
						</button>
					</span>
				</div>
			</div>
		</div>
	`;

export class SubstituteSelection extends HTMLElement {

	public static readonly EVENT_START_LOADING = "substituteSelection.startLoading";
	public static readonly EVENT_FINISHED_LOADING = "substituteSelection.finishedLoading";
	public static readonly EVENT_SUBSTITUTE_SELECTED = "substituteSelection.substituteSelected";
	public static readonly EVENT_SUBSTITUTE_SELECTION_VISIBILITY_TOGGLED = "substituteSelection.substituteSelectionVisibilityToggled";
	public static readonly EVENT_USER_SELECTED = "substituteSelection.userSelected";

	private substituteCombobox: Combobox<PartialUser>;
	private userId: number;
	private workingTimeType: PartialWorkingTimeType | null;
	private substitute?: PartialUser;
	private isSubstituteDeleted: boolean;
	private requiresSubstitute: boolean | null = null;

	private findPossibleSubstitutesRequest: JQuery.jqXHR<PartialUser[]>;
	private requiresSubstituteRequest?: JQuery.jqXHR<IsSubstituteRequiredResponse>;

	public init() {
		this.innerHTML = template;
		this.workingTimeType = null;
		// Unknown, not "not required" - so the first setWorkingTimeType() call after init still computes it
		// instead of leaving the field hidden.
		this.requiresSubstitute = null;
		this.updateVisibility();
		this.initSubstituteCombobox();
	}

	public setUser(userId: number) {
		this.userId = userId;
		this.fireEvent(SubstituteSelection.EVENT_USER_SELECTED, userId);
		this.filterAndFillSubstituteCombobox();
		this.updateSubstituteRequirement();
	}

	public setWorkingTimeType(workingTimeType: PartialWorkingTimeType | null) {
		const typeChanged = this.workingTimeType != null && this.workingTimeType.id !== workingTimeType?.id;
		this.workingTimeType = workingTimeType;
		if (typeChanged) {
			// The working time type was changed → the requirement must be recomputed from the current configuration.
			this.requiresSubstitute = null;
		}
		this.updateSubstituteRequirement();
	}

	/**
	 * @param requiresSubstitute the entity's known requirement. Pass it for an existing entity so the
	 * requirement is used as-is and not recomputed from the current — possibly changed — working time
	 * type / group override. Leave it {@code null} for a new entity so the requirement is computed from the current
	 * configuration via {@link setWorkingTimeType} / {@link setUser}.
	 */
	public setSubstitute(substitute?: PartialUser, requiresSubstitute: boolean | null = null) {
		this.substitute = substitute;
		this.isSubstituteDeleted = !substitute && !!requiresSubstitute;
		// Reset the tracked working time type so the one selected right after (during load) counts as the
		// initial selection rather than a change, letting the requirement below stand until the user
		// actually switches the type.
		this.workingTimeType = null;
		this.requiresSubstitute = requiresSubstitute;
		if (substitute) {
			this.fireEvent(SubstituteSelection.EVENT_SUBSTITUTE_SELECTED, substitute);
		}
		this.filterAndFillSubstituteCombobox();
		this.updateSubstituteRequirement();
	}

	public setRequiresSubstitute(requiresSubstitute: boolean) {
		this.requiresSubstitute = requiresSubstitute;
		if (!this.requiresSubstitute) {
			// Only clear the substitute if not required
			this.substituteCombobox.clearSelection(true);
		} else if (this.isSubstituteDeleted) {
			// Fresh requiresSubstitute arrived after fillSubstituteCombobox already ran — apply the deleted message now
			this.substituteCombobox?.setEmptyMessage(messages["substituteSelection.substituteNoLongerAvailable"]);
			this.substituteCombobox?.clearSelection(true);
		}
		this.updateVisibility();
	}

	/**
	 * Refreshes the "substitute required" state for the currently selected working time type and
	 * user. Toggles the substitute combobox between visible and hidden, and clears the currently
	 * selected substitute when the requirement drops to false (see {@link setRequiresSubstitute}),
	 * so the user knows up-front whether a substitute has to be picked.
	 *
	 * This is a UX convenience only. The authoritative decision is made by the backend on save via
	 * {@code WorkingTimeTypeService.isSubstituteRequired}, which evaluates the type flag together
	 * with any group-level override; the backend result is what ends up on the persisted entity
	 * regardless of what the frontend computed here.
	 *
	 */
	private updateSubstituteRequirement() {
		this.requiresSubstituteRequest?.abort();

		if (this.requiresSubstitute !== null) {
			// The requirement is known (an existing entity's stored value, or one we already computed) →
			// just reflect it without contacting the endpoint.
			this.updateVisibility();
			return;
		}

		const workingTimeType = this.workingTimeType;
		if (!workingTimeType) {
			// No working time type selected yet → keep the requirement unknown (hidden) until one is set.
			this.updateVisibility();
			return;
		}
		if (workingTimeType.editUnit === DurationUnit.Minutes) {
			this.setRequiresSubstitute(false);
			return;
		}
		const requestUserId = this.userId;
		if (!requestUserId) {
			this.setRequiresSubstitute(workingTimeType.requiresSubstitute);
			return;
		}
		this.fireEvent(SubstituteSelection.EVENT_START_LOADING);
		this.requiresSubstituteRequest = api("workingTimeTypes/isSubstituteRequired.json")
			.get({userId: requestUserId, workingTimeTypeId: workingTimeType.id})
			.done(({requiresSubstitute}) => this.setRequiresSubstitute(requiresSubstitute))
			.always(() => this.fireEvent(SubstituteSelection.EVENT_FINISHED_LOADING));
	}

	public getSelectedUser(): PartialUser | null {
		return this.substituteCombobox.getSelectedItem();
	}

	private updateVisibility() {
		// requiresSubstitute is null while the requirement is still unknown → treat that as "hidden".
		this.toggle(this, this.requiresSubstitute ?? false);
		this.fireEvent(SubstituteSelection.EVENT_SUBSTITUTE_SELECTION_VISIBILITY_TOGGLED, this.requiresSubstitute ?? false);
	}

	private filterAndFillSubstituteCombobox() {
		if (this.userId) {
			this.findPossibleSubstitutesRequest?.abort();
			this.fireEvent(SubstituteSelection.EVENT_START_LOADING);
			this.findPossibleSubstitutesRequest = api("workingTimeDateSpans/findPossibleSubstitutes.json")
				.get({userId: this.userId})
				.done((users: PartialUser[]) => {
					this.fillSubstituteCombobox(users ?? []);
				})
				.always(() => this.fireEvent(SubstituteSelection.EVENT_FINISHED_LOADING));
		} else {
			this.fillSubstituteCombobox();
		}
	}

	private fillSubstituteCombobox(users: PartialUser[] = []) {
		// Set default empty message
		// set empty message doesn't update the combobox
		const emptyMessage = users?.length === 0 ? messages["substituteSelection.noSubstitute"] : messages["substituteSelection.selectSubstitute"];
		this.substituteCombobox?.setEmptyMessage(emptyMessage);
		// Update items and add substitute if necessary
		this.substituteCombobox?.updateItems(users, this.substitute, false, true);
		if (this.isSubstituteDeleted) {
			// Substitute user got removed -> show message instead of next user in list
			this.substituteCombobox?.setEmptyMessage(messages["substituteSelection.substituteNoLongerAvailable"]);
			this.substituteCombobox?.clearSelection(true);    // Force clear selection to force the message
		}
	}

	private initSubstituteCombobox() {
		this.substituteCombobox = new Combobox<PartialUser>({
			items: [],
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $(this).find("[data-substitute-combobox-id]"),
			filterElement: $(this).find("[data-substitute-combobox-filter]"),
			caretElement: $(this).find("[data-substitute-combobox-caret]"),
			clearable: false,
			emptyMessage: messages["substituteSelection.selectSubstitute"],
		});
		$(this.substituteCombobox).on(Combobox.EVENT_CHANGED, (e, substitute: PartialUser) => {
			this.fireEvent(SubstituteSelection.EVENT_SUBSTITUTE_SELECTED, substitute);
		});
	}

	private fireEvent<T>(eventName: string, detail?: T) {
		this.dispatchEvent(new CustomEvent<T>(eventName, {
			detail: detail,
		}));
	}

	private toggle(element: HTMLElement, showOrHide: boolean) {
		element.style.display = showOrHide ? 'block' : 'none';
	}
}
